import React from "react";
import Immutable from "immutable";
import { Input, Switch, Select, SelectOption, Text, Button, Box } from "@showitinc/showit-wc";

import AppConstants from "../../../constants/AppConstants";
import { PAGINATED_TEMPLATE_TYPES } from "../../../constants/WP";
import When from "../../../components/utils/When";
import BlockUtils from "../../../utils/BlockUtils";
import UIActions from "../../../actions/UIActions";
import SiteDesignStore from "../../../stores/SiteDesignStore";
import WPCustomPostTypeFilter from "../../../components/utils/WPCustomPostTypeFilter";
import { getWpCustomPostTypeFilter, formatPropUpdate } from "../../../utils/PropertyUtils";
import NameSlugInput from "../../../components/NameSlugInput";
import { INPUT_TYPE_NUMBER } from "../../../constants/ElementConstants";
import styles from "./BlockPropertiesInfo.module.css";
import { initFormatter } from "../../../utils/PropertyFormatters";

const {
  BLOCK_WP,
  IS_CPT,
  WP_CPT_SLUG,
  CPT,
  STANDARD_POST_TYPE,
  CUSTOM_POST_TYPE,
  UPDATE_ACTION_DELETE,
  WP_CAT,
  WP_TAG,
  UPDATE_ACTION_SET,
  WP_CPT_FILTER_TAXONOMY,
  WP_CPT_FILTERS,
  WP_BLOCK_POST,
  WP_BLOCK_NEXT_POST,
  ITEM_TYPE_BLOCK,
  BLOCK_PROP_PARENT_BLOCK,
  WP_BLOCK_POSTS_VIEW_LOOKUP,
  WP_DEFAULT_POST_COUNT,
  PARSE_INT_DEFAULT_RADIX,
} = AppConstants;

const exemptPaginatedWordpressTypes = {
  [WP_BLOCK_POST]: true,
  [WP_BLOCK_NEXT_POST]: true,
};

const BlockPropertiesInfo = ({ onPropUpdate, blockId, block, name, slug, isWP }) => {
  const page = SiteDesignStore.getCurrentPage();
  const wpTemplate = page.data.getIn([BLOCK_WP, "template"], "");
  const _onToggle = (propInfo) => {
    if (onPropUpdate) {
      const { value } = propInfo;
      onPropUpdate({ ...propInfo, value: value ? 1 : null });
    }
  };
  const { onChange: toggle } = initFormatter(_onToggle);
  const { onChange } = initFormatter(onPropUpdate);

  const _onPropUpdate = (updates) => {
    if (onPropUpdate) {
      onPropUpdate(updates);
    }
  };

  const _onWPUpdate = (propName, newValue, origValue) => {
    const isStaticContent = newValue === "";
    onChange(isStaticContent ? BLOCK_WP : propName, origValue)(isStaticContent ? null : newValue);

    // If the wordpress type doesn't support pagination, set showOnFirst option to off
    if (exemptPaginatedWordpressTypes[newValue]) {
      onChange("wp.showOnFirst", undefined)(null);
    }
  };

  const onPostTypeChange = (propName, newValue, origValue) => {
    // If post type is custom, delete standard post type properties. If standard, delete custom post type properties.
    const unusedProps =
      parseInt(newValue, PARSE_INT_DEFAULT_RADIX) === CUSTOM_POST_TYPE
        ? [
            { name: `${BLOCK_WP}.${WP_CAT}`, action: UPDATE_ACTION_DELETE },
            { name: `${BLOCK_WP}.${WP_TAG}`, action: UPDATE_ACTION_DELETE },
            { name: `${BLOCK_WP}.${CPT}`, action: UPDATE_ACTION_SET, value: {} },
          ]
        : [
            { name: `${BLOCK_WP}.${CPT}`, action: UPDATE_ACTION_DELETE },
            { name: `${BLOCK_WP}.${IS_CPT}`, action: UPDATE_ACTION_DELETE },
          ];
    _onPropUpdate([...unusedProps, formatPropUpdate(propName, newValue, origValue)]);
  };

  const _addCptFilter = () => {
    const origValue = block.getIn([BLOCK_WP, CPT, WP_CPT_FILTERS], null);
    const filters = !origValue || !Immutable.List.isList(origValue) ? Immutable.List() : origValue;

    // Default to category
    let filterType = WP_CAT;
    const hasCategory = filters.some((f) => f.get("type") === WP_CAT);
    const hasTag = filters.some((f) => f.get("type") === WP_TAG);

    // If both category and tag exist in cpt filters, set to taxonomy filter
    if (hasCategory && hasTag) {
      filterType = WP_CPT_FILTER_TAXONOMY;
    } else if (hasCategory) {
      // if tag is not in filters and category is, set to tag
      filterType = WP_TAG;
    }

    const newFilter = getWpCustomPostTypeFilter(filterType);
    const newValue = filters.unshift(newFilter);

    onChange(
      `${BLOCK_WP}.${CPT}.${WP_CPT_FILTERS}`,
      origValue,
      true,
      false,
      undefined,
      UPDATE_ACTION_SET
    )(newValue);
  };

  const _toggleRenamePopup = () => {
    UIActions.toggleRenamePopup({
      title: "Canvas",
      itemType: ITEM_TYPE_BLOCK,
      itemId: blockId,
      name,
      slug,
      showWarning: true,
    });
  };

  const _selectSiteBlock = (e) => {
    const baseBlock = block.get(BLOCK_PROP_PARENT_BLOCK);

    UIActions.selectSiteBlock({ block: baseBlock });
    UIActions.addActiveItemHistory({
      type: ITEM_TYPE_BLOCK,
    });
  };

  const _getSiteBlockInfo = () => {
    const siteBlock = block.get(BLOCK_PROP_PARENT_BLOCK);

    return (
      <div>
        <Text className="panel-subheading">Site Canvas</Text>
        <Input value={siteBlock.get("name")} label="Name" disabled reserveContext />
        <Button
          variant="outline"
          fullWidth
          className={styles["edit-site-canvas-button"]}
          onClick={_selectSiteBlock}
        >
          Edit Site Canvas
        </Button>
      </div>
    );
  };

  const wpType = block.getIn([BLOCK_WP, "type"], "");
  const isCustomPostType = parseInt(block.getIn([BLOCK_WP, IS_CPT], 0));
  const cptFilters = block.getIn([BLOCK_WP, CPT, WP_CPT_FILTERS], Immutable.List());
  const activeFilters = cptFilters.map((filter) => filter.get("type"));
  const stackViews = block.getIn([BLOCK_WP, "stackViews"], false);
  const showOnce = block.getIn([BLOCK_WP, "showOnce"], false);
  const showOnFirst = block.getIn([BLOCK_WP, "showOnFirst"], false);

  let queryProps = null;

  if (isWP && wpType === WP_BLOCK_POSTS_VIEW_LOOKUP) {
    queryProps = (
      <div>
        <Text className="panel-subheading">Blog Post Lookup</Text>
        <Select
          value={String(isCustomPostType)}
          label="Post Type"
          data-testid="blog-post-lookup-post-type-select"
          reserveContext
          shift="left"
          onSISelect={(e) =>
            onPostTypeChange(`${BLOCK_WP}.${IS_CPT}`, e.target.value, isCustomPostType)
          }
        >
          <SelectOption value={STANDARD_POST_TYPE}>Standard</SelectOption>
          <SelectOption value={CUSTOM_POST_TYPE}>Custom</SelectOption>
        </Select>
        {isCustomPostType ? (
          <Input
            label="Post Type Slug"
            data-testid="post-type-slug-input"
            placeholder="my_podcasts"
            onSIChange={onChange(
              `${BLOCK_WP}.${CPT}.${WP_CPT_SLUG}`,
              block.getIn([BLOCK_WP, CPT, WP_CPT_SLUG])
            )}
            value={block.getIn([BLOCK_WP, CPT, WP_CPT_SLUG], "")}
            reserveContext
          />
        ) : (
          <>
            <Input
              label="Category Slug"
              data-testid="category-slug-input"
              placeholder="my_category"
              onSIChange={onChange("wp.cat", block.getIn(["wp", "cat"]))}
              value={block.getIn(["wp", "cat"], "")}
              reserveContext
            />
            <Input
              label="Tag Name Slug"
              data-testid="tag-name-slug-input"
              placeholder="my_tag"
              onSIChange={onChange("wp.tag", block.getIn(["wp", "tag"]))}
              value={block.getIn(["wp", "tag"], "")}
              reserveContext
            />
          </>
        )}
        <Input
          type={INPUT_TYPE_NUMBER}
          data-testid="post-count-input"
          label="Number of Posts"
          value={block.getIn([BLOCK_WP, "count"], WP_DEFAULT_POST_COUNT)}
          step="1"
          onSIBeforeChange={onChange(
            "wp.count",
            block.getIn([BLOCK_WP, "count"]),
            true,
            false,
            WP_DEFAULT_POST_COUNT
          )}
          onSIInput={onChange(
            "wp.count",
            block.getIn([BLOCK_WP, "count"]),
            false,
            false,
            WP_DEFAULT_POST_COUNT
          )}
          reserveContext
        />
        <Input
          type={INPUT_TYPE_NUMBER}
          data-testid="offset-input"
          label="Offset"
          value={block.getIn([BLOCK_WP, "offset"], "")}
          step="1"
          onSIBeforeChange={onChange("wp.offset", block.getIn([BLOCK_WP, "offset"]))}
          onSIInput={onChange("wp.offset", block.getIn([BLOCK_WP, "offset"]), false)}
          reserveContext
        />
        <Select
          value={block.getIn([BLOCK_WP, "orderby"], "")}
          label="Order By"
          data-testid="order-by-select"
          reserveContext
          shift="left"
          onSISelect={onChange("wp.orderby", block.getIn([BLOCK_WP, "orderby"]))}
        >
          <SelectOption value="">Date</SelectOption>
          <SelectOption value="comments">Comment Count</SelectOption>
          <SelectOption value="id">Post ID</SelectOption>
          <SelectOption value="title">Title</SelectOption>
          <SelectOption value="name">Slug</SelectOption>
          <SelectOption value="modified">Modified Date</SelectOption>
        </Select>

        <Select
          value={block.getIn([BLOCK_WP, "order"], "")}
          label="Direction"
          reserveContext
          shift="left"
          onSISelect={onChange("wp.order", block.getIn([BLOCK_WP, "order"]))}
        >
          <SelectOption value="">Descending</SelectOption>
          <SelectOption value="ASC">Ascending</SelectOption>
        </Select>
        {!!isCustomPostType && (
          <Box mb="2">
            <Button
              fullWidth
              variant="minimal"
              data-testid="add-cpt-filter-button"
              onClick={_addCptFilter}
              iconPrefix="add"
            >
              Filter
            </Button>
          </Box>
        )}
        {!!isCustomPostType &&
          cptFilters.map((filter, i) => (
            <WPCustomPostTypeFilter
              index={i}
              filter={filter}
              filterId={`${BLOCK_WP}.${CPT}.${WP_CPT_FILTERS}.${i}`}
              activeFilters={activeFilters}
              onPropUpdate={_onPropUpdate}
              key={`f${i}`}
            />
          ))}
      </div>
    );
  } else if (isWP && wpType === WP_BLOCK_POST) {
    queryProps = (
      <>
        <Switch
          label="Combine Views as Posts"
          data-testid="combine-views-as-posts-switch"
          checked={stackViews}
          reserveContext
          onSIChange={toggle("wp.stackViews", stackViews)}
        />
        <Switch
          label="Show this canvas once per page"
          data-testid="show-once-switch"
          checked={showOnce}
          reserveContext
          helpTooltip="show-once"
          onSIChange={toggle("wp.showOnce", showOnce)}
        />
      </>
    );
  }

  const usingSiteBlock = BlockUtils.hasSiteBlock(block);

  return (
    <>
      <NameSlugInput
        propName="name"
        name={name}
        slug={slug}
        labelPrefix=""
        onAreaFocus={_toggleRenamePopup}
      />

      <Input
        label="Stacking Order"
        value={block.get("z", 0)}
        type={INPUT_TYPE_NUMBER}
        data-testid="stacking-order-input"
        min="0"
        step="1"
        onSIBeforeChange={onChange("z")}
        onSIInput={onChange("z", false)}
        reserveContext
      />

      <When it={!!isWP}>
        <Select
          value={wpType}
          data-testid="wp-type-select"
          label="WordPress"
          reserveContext
          shift="left"
          onSISelect={(e) => _onWPUpdate("wp.type", e.target.value, wpType)}
        >
          <SelectOption value="">Static Content</SelectOption>
          <SelectOption value={WP_BLOCK_POST}>In Post Loop</SelectOption>
          <SelectOption value={WP_BLOCK_NEXT_POST}>Next Post Start</SelectOption>
          <SelectOption value={WP_BLOCK_POSTS_VIEW_LOOKUP}>Posts View Lookup</SelectOption>
        </Select>
      </When>
      <When
        it={
          isWP && !!PAGINATED_TEMPLATE_TYPES[wpTemplate] && !exemptPaginatedWordpressTypes[wpType]
        }
      >
        <Switch
          checked={showOnFirst}
          label="Hide on pagination"
          data-testid="hide-on-pagination-switch"
          onSIChange={toggle("wp.showOnFirst", showOnFirst)}
        />
      </When>
      {queryProps}
      {usingSiteBlock && _getSiteBlockInfo()}
    </>
  );
};

export default BlockPropertiesInfo;
